import moment, { Duration } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLotteryCounter } from '../../../Store/Slicers/LotteryCountersSlice';
import { isLttry } from '../../../utils/template';

export type Props = {
    drawLoop?: number;
    nextDrawDate: string;
    nextNextDrawDate?: string;
    lotterySlug?: string; // Optional parameter, if passed, then data from API will override other settings
    isBannerCounter?: string;
};
function DrawDateCounter({ drawLoop = 0, nextDrawDate, nextNextDrawDate = null, lotterySlug, isBannerCounter }: Props) {
    const { t } = useTranslation();
    const isTemplateLttry = useMemo(isLttry, []);

    const [nextDraw, setNextDraw] = useState<string>();
    const [nextNextDraw, setNextNextDraw] = useState<string>();
    const [loopInterval, setLoopInterval] = useState<number>();

    const [difference, setDifference] = useState<Duration>(null);
    const [counterRunning, setCounterRunning] = useState<boolean>(true);

    const { counter, isLoading } = useLotteryCounter(lotterySlug);

    useEffect(() => {
        // Use data passed by props
        if (!lotterySlug) {
            // Prevent re-assign
            if (nextDraw === nextDrawDate) {
                return;
            }

            setNextDraw(nextDrawDate);
            setNextNextDraw(nextNextDrawDate);
            setLoopInterval(Number(drawLoop));
        }

        // Use fetched data
        if (counter === null) {
            return;
        }

        setLoopInterval(counter.drawLoop);
        setNextDraw(counter.nextDrawDate);
        setNextNextDraw(counter.nextNextDrawDate);
    }, [isLoading]);

    useEffect(() => {
        const interval = setInterval(() => {
            const now = moment();
            const nextDrawTime = moment(nextDraw);
            const diff = moment.duration(nextDrawTime.diff(now));
            if (diff.milliseconds() < 0) {
                clearInterval(interval);

                if (loopInterval > 0) {
                    setNextDraw(moment().add(loopInterval, 'seconds').toISOString());
                } else if (nextNextDraw != null) {
                    setNextDraw(nextNextDraw);
                } else {
                    setCounterRunning(false);
                }
            } else if (diff.isValid()) {
                setDifference(diff);
            } else {
                setDifference(null);
                clearInterval(interval);
            }
        }, 500);

        return () => {
            clearInterval(interval);
        };
    }, [nextDraw, loopInterval]);

    function renderDigitsInSpan(digits) {
        return String(digits)
            .padStart(2, '0')
            .split('')
            .map((digit, index) => <span key={index}>{digit}</span>);
    }

    function printDays() {
        if (difference.days() < 1) {
            return null;
        }
        return (
            <span className="counter-container">
                <span className="counter-days">{renderDigitsInSpan(difference.days())}</span>
                <span className="counter-days-units">
                    {difference.days() === 1 ? t('counter.day') : t('counter.days')}
                </span>
                {isTemplateLttry && <span className="counter-and">{t('counter.and')}</span>}
            </span>
        );
    }

    function printHours() {
        if (difference.hours() < 1) {
            return null;
        }

        if (difference.hours() === 0 && difference.days() > 0 && isTemplateLttry) {
            return null;
        }

        return (
            <span className="counter-container">
                <span className="counter-hours">{renderDigitsInSpan(difference.hours())}</span>
                <span className="counter-hours-units">
                    {difference.hours() === 1 ? t('counter.hour') : t('counter.hours')}
                </span>
                {difference.days() <= 0 && isTemplateLttry && <span className="counter-and">{t('counter.and')}</span>}
            </span>
        );
    }

    function printMinutes() {
        if (difference.days() > 0 && (isBannerCounter !== 'true')) {
            return null;
        }

        let label = t('counter.minutes');
        if (isTemplateLttry) {
            label = t('counter.min');
        } else if (difference.minutes() === 1) {
            label = t('counter.minute');
        }

        return (
            <span className="counter-container">
                <span className="counter-minutes">{renderDigitsInSpan(difference.minutes())}</span>
                <span className="counter-minutes-units">{label}</span>
            </span>
        );
    }

    function printSeconds() {
        if (difference.days() > 0) {
            return null;
        }

        if (difference.hours() >= 1 && (isBannerCounter !== 'true' || difference.days() !== 0)) {
            return null;
        }

        let label = t('counter.seconds');
        if (isTemplateLttry) {
            label = t('counter.sec');
        } else if (difference.seconds() === 1) {
            label = t('counter.second');
        }

        return (
            <>
                {isTemplateLttry && <span className="counter-and">{t('counter.and')}</span>}
                <span className="counter-container">
                    <span className="counter-seconds">{renderDigitsInSpan(difference.seconds())}</span>
                    <span className="counter-seconds-units">{label}</span>
                </span>
            </>
        );
    }

    if (!difference) {
        return null;
    }

    if (!counterRunning && difference.milliseconds() < 0) {
        return <>{t('pending')}</>;
    }

    return (
        <>
            {printDays()}
            {printHours()}
            {printMinutes()}
            {printSeconds()}
        </>
    );
}

export default DrawDateCounter;
